import AvailableLayoutsCard from '@/components/layout/AvailableLayoutsCard';
import CardIconButton from '@/components/layout/CardIconButton';
import ColorIndicator from '@/components/layout/ColorIndicator';
import DisplayTypeCard from '@/components/layout/DisplayTypeCard';
import LayoutDialog from '@/components/layout/LayoutDialog';
import LayoutInfoCard from '@/components/layout/LayoutInfoCard';
import { LAYOUT_TYPE } from '@/hooks/layouts/defaultState';
import { useLayoutUtils } from '@/hooks/layouts/useLayoutUtils';
import { useAppContext } from '@/provider/contexts';
import { useLayoutState } from '@/provider/layouts/layoutContext';
import { LAYOUT_MODE, FullLayoutItem, LayoutDialogResult, LayoutList, COLOR_OPTION } from '@/types/pages/layout';
import { parseSizeAndColorsFromXml } from '@/utils/layouts/layoutUtils';
import { Edit, Delete } from '@mui/icons-material';
import { Box, Chip, CircularProgress } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
type SingleLayoutProps = {
    viewMode?: boolean;
};
const SingleLayout = ({ viewMode }: SingleLayoutProps) => {
    const { t } = useTranslation('common');
    const { setNotification } = useAppContext();
    const { state, dispatch } = useLayoutState();
    const { optionsWithLabel, formateSingleInfo } = useLayoutUtils();
    const {
        relations,
        displayType,
        layoutsById,
        searchSub,
        selectedColors,
        selectedLayouts,
        editLayout,
        selectedSizes,
    } = useMemo(() => state.single, [state.single]);
    const nextTempId = useRef(1);

    useEffect(() => {
        let payload = null;
        if (state.layoutInfo) {
            const { layouts, relations, maxExisting } = formateSingleInfo();
            nextTempId.current = maxExisting + 1;
            payload = {
                layouts,
                relations,
            };
        }

        dispatch({ type: 'INIT_SINGLE', payload });
    }, [state.layoutInfo, state.displayOptions]);

    const associatedLayouts = useMemo(() => {
        const ids = relations[displayType]?.list || [];
        return ids;
    }, [relations, displayType]);

    const defaultLayoutId = useMemo(() => {
        return relations[displayType]?.default_layout || null;
    }, [relations, displayType]);

    const handleReorder = (newList: LayoutList[]) => {
        dispatch({ type: 'REORDER_SINGLE', newList });
    };

    const handleDetach = (ids: string[]) => {
        dispatch({ type: 'DETACH_SINGLE', ids });
    };

    // Available
    const filteredLayouts = useMemo(() => {
        return Object.values(layoutsById).filter((layout) => {
            if (searchSub && !layout.name.toLowerCase().includes(searchSub.toLowerCase())) return false;
            if (selectedColors.length > 0 && !selectedColors.includes(String(layout.bpp))) return false;
            if (selectedSizes.length > 0 && !selectedSizes.includes(layout.size)) return false;
            return true;
        });
    }, [layoutsById, relations, displayType, searchSub, selectedColors, selectedSizes]);

    const sizeOptions = useMemo(
        () =>
            Array.from(new Set(Object.values(layoutsById).map((l) => l.size))).map((s) => ({
                value: s,
                label: s,
            })),
        [layoutsById],
    );

    const layoutNames = useMemo(() => Object.values(layoutsById).map((o) => o.name), [layoutsById]);

    const requiredResolution = useMemo(() => {
        const option = state.displayOptions.find((c) => c.value === displayType);
        return option?.size ?? '';
    }, [state.displayOptions, displayType]);

    const handleAttach = () => {
        const valid = filteredLayouts.filter(
            (l) => selectedLayouts.includes(l.id) && parseFloat(l.size) === parseFloat(requiredResolution),
        );
        const invalid = filteredLayouts.filter(
            (l) => selectedLayouts.includes(l.id) && parseFloat(l.size) !== parseFloat(requiredResolution),
        );

        if (invalid.length > 0) {
            setNotification({
                message: t('layoutsNotAttached', {
                    count: invalid.length,
                    size: requiredResolution,
                }),
                severity: 'warning',
            });
        }
        if (valid.length > 0) {
            const attachIds = valid.map((l) => ({
                id: l.id,
                name: l.name,
                uuid: `single-${l.id}-${crypto.randomUUID()}`,
            }));

            dispatch({
                type: 'SET_SINGLE_RELATIONS',
                payload: {
                    displayType: displayType,
                    selectedList: attachIds,
                },
            });
        }
        dispatch({ type: 'CLEAN_SINGLE_SELECTED_LAYOUT' });
    };

    const handleDeleteAll = () => {
        dispatch({ type: 'DELETE_SINGLE_LAYOUT_PACKAGE' });
    };

    const handleEditChange = (layout: FullLayoutItem) => (_event: React.MouseEvent) => {
        _event.stopPropagation();
        dispatch({
            type: 'UPDATE_SINGLE_EDIT_DATA',
            payload: layout,
        });
    };
    const editData = useMemo(() => {
        if (editLayout) {
            const { name, purpose, xmlContent } = editLayout;
            return {
                name,
                purpose: Number(purpose),
                content: xmlContent,
            };
        }
        return undefined;
    }, [editLayout]);

    const handleUpdateLayout = (updated: LayoutDialogResult) => {
        if (!editLayout) return;
        const { size, colors, bpp } = parseSizeAndColorsFromXml(updated.content);
        dispatch({
            type: 'STORE_SINGLE_EDIT_DATA',
            payload: {
                id: editLayout.id,
                colors,
                updated,
                size,
                bpp,
            },
        });
    };

    const handleAddNewLayout = ({ name, purpose, content }: LayoutDialogResult) => {
        const { size, colors, bpp } = parseSizeAndColorsFromXml(content);
        const newNumId = nextTempId.current++;
        const newIdStr = String(newNumId);

        const newLayout = {
            [newNumId]: {
                id: newIdStr,
                name,
                description: '',
                images: [],
                number: newIdStr,
                size,
                colors,
                purpose: String(purpose),
                xmlContent: content,
                bpp,
            },
        };

        dispatch({ type: 'ADD_NEW_SINGLE_LAYOUT', payload: newLayout });
    };

    const handleDeleteChange = (layout: FullLayoutItem) => (_event: React.MouseEvent) => {
        _event.stopPropagation();
        dispatch({
            type: 'DELETE_SINGLE_LAYOUT',
            payload: layout.id,
        });
    };

    const handleSetDefault = (uuid: string) => {
        dispatch({
            type: 'SET_SINGLE_DEFAULT',
            payload: uuid,
        });
    };
    if (state.loadings.layout || state.loadings.category) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: { xs: 'block', md: 'grid' },
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2,
                }}
            >
                <DisplayTypeCard
                    viewMode={viewMode}
                    mode={LAYOUT_MODE.SINGLE}
                    options={optionsWithLabel}
                    value={displayType}
                    onChange={(dt) => dispatch({ type: 'SET_SINGLE_DISPLAY', payload: dt })}
                    layouts={associatedLayouts}
                    onView={(id) => {
                        const el = document.getElementById(`layout-card-${LAYOUT_MODE.SINGLE}-${id}`);
                        if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            el.classList.add('flash-highlight');
                            setTimeout(() => el.classList.remove('flash-highlight'), 600);
                        }
                    }}
                    onReorder={handleReorder}
                    onDetach={handleDetach}
                    onSetDefault={handleSetDefault}
                    defaultLayoutId={defaultLayoutId}
                />

                <AvailableLayoutsCard<FullLayoutItem>
                    viewMode={viewMode}
                    tabType={LAYOUT_MODE.SINGLE}
                    layouts={filteredLayouts}
                    enableColor
                    selectedColors={selectedColors}
                    onColorChange={(newColors) => dispatch({ type: 'SET_SINGLE_SELECTED_COLORS', payload: newColors })}
                    colorOptions={COLOR_OPTION}
                    enableSize
                    sizeOptions={sizeOptions}
                    selectedSizes={selectedSizes}
                    onSizeChange={(newSizes) => dispatch({ type: 'SET_SINGLE_SELECTED_SIZE', payload: newSizes })}
                    searchText={searchSub}
                    onSearchChange={(text) => dispatch({ type: 'SET_SINGLE_SEARCH_SUB', payload: text })}
                    onAttach={handleAttach}
                    onDeleteAll={handleDeleteAll}
                    onAddNew={() => dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'new', open: true } })}
                >
                    {(layout) => (
                        <LayoutInfoCard
                            id={`layout-card-${LAYOUT_MODE.SINGLE}-${layout.id}`}
                            key={`${layout.id}-${LAYOUT_MODE.SINGLE}`}
                            images={layout.images}
                            name={layout.name}
                            description={layout.description}
                            checked={viewMode ? undefined : selectedLayouts.includes(layout.id)}
                            onCheckedChange={
                                viewMode
                                    ? undefined
                                    : (isChecked) =>
                                          dispatch({
                                              type: 'TOGGLE_SINGLE_SELECTED_LAYOUT',
                                              payload: {
                                                  id: layout.id,
                                                  checked: isChecked,
                                              },
                                          })
                            }
                            sx={{ scrollMarginBlock: '200px' }}
                        >
                            <LayoutInfoCard.Chips>
                                <Chip
                                    size="small"
                                    label={t('Size {{size}}', { size: layout.size })}
                                    variant="outlined"
                                    color="primary"
                                />
                                <Chip
                                    size="small"
                                    label={t('Layout No. {{number}}', { number: layout.number })}
                                    variant="outlined"
                                    color="primary"
                                />
                                {layout.colors.map((c, i) => (
                                    <ColorIndicator key={i} color={c} />
                                ))}
                            </LayoutInfoCard.Chips>
                            {!viewMode && (
                                <LayoutInfoCard.Actions>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <CardIconButton color="primary" onClick={handleEditChange(layout)}>
                                            <Edit />
                                        </CardIconButton>
                                        <CardIconButton color="error" onClick={handleDeleteChange(layout)}>
                                            <Delete />
                                        </CardIconButton>
                                    </Box>
                                </LayoutInfoCard.Actions>
                            )}
                        </LayoutInfoCard>
                    )}
                </AvailableLayoutsCard>
            </Box>
            <LayoutDialog
                open={state.dialogs.new && state.currentTab === LAYOUT_TYPE.SINGLE}
                layoutNames={layoutNames}
                dialogTitle={t('New Layout')}
                onSave={handleAddNewLayout}
                onClose={() => dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'new', open: false } })}
            />
            <LayoutDialog
                open={state.dialogs.edit && state.currentTab === LAYOUT_TYPE.SINGLE}
                layoutNames={layoutNames}
                dialogTitle={t('Edit Layout')}
                initialData={editData}
                onSave={handleUpdateLayout}
                onClose={() => {
                    dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'edit', open: false } });
                    dispatch({ type: 'CLEAR_SINGLE_EDIT_DATA' });
                }}
                showTopRightClose={false}
            />
        </>
    );
};

export default SingleLayout;
